const createDraftText = (text, fontSize = "FONT_SIZE_18", align = "left", color = "#ffffff") =>
  JSON.stringify({
    blocks: [
      {
        key: Math.random().toString(36).substring(2, 7),
        text,
        type: "unstyled",
        depth: 0,
        inlineStyleRanges: [
          { offset: 0, length: text.length, style: fontSize },
          { offset: 0, length: text.length, style: `color-${color}` },
        ],
        entityRanges: [],
        data: { "text-align": align },
      },
    ],
    entityMap: {},
  });

const createStep = ({ start, end, kenBurns, tag, title, body, tagColor = "#93C5FD", bg = "#0F172AD8" }) => {
  const isCenter = (start === 4 && end === 10) || (start === 4 && end === 6);
  const align = isCenter ? "center" : "left";
  return {
    backgroundColor: bg,
    borderRadius: true,
    horizontalAlign: "center",
    startColumn: start,
    endColumn: end,
    stepPosition: "bottom",
    kenBurns,
    blocks: [
      ...(tag ? [{ type: "text", text: createDraftText(tag, "FONT_SIZE_12", align, tagColor) }] : []),
      { type: "text", text: createDraftText(title, isCenter ? "FONT_SIZE_24" : "FONT_SIZE_20", align, "#FFFFFF") },
      { type: "text", text: createDraftText(body, "FONT_SIZE_16", align, "#CBD5E1") },
    ],
  };
};

export const demo11_ken_burns = {
  appBar: {
    backgroundColor: "#0F172A",
    mode: "dark",
    exhibition: {
      name: "Nærbilder fra fotohistorien – Ken Burns Demo",
      description: "Utforsk historiske fotografier med dynamisk fokus og filmatisk zoom-overgang.",
      url: "https://beta.virtueltmuseum.no",
    },
    museum: { name: "Virtuelt Museum", url: "https://beta.virtueltmuseum.no" },
  },
  autoplay: { enabled: true },
  audioGuide: { enabled: false },
  sections: [
    {
      order: 1,
      type: "sticky",
      templateId: 5,
      title: "Fotoutstyr på 1920-tallet",
      backgroundImage: "013AkPNRPrZ6",
      backgroundFocus: { x: 0, y: 0.3 },
      gridSize: "twelveColumns",
      containers: [
        createStep({
          start: 4, end: 10, kenBurns: { x: 0, y: 0.2, zoom: 1.0 },
          tag: "FOTOHISTORIE I DETALJ", title: "Det store atelierkameraet",
          body: "Scroll nedover for å oppleve Ken Burns-effekten. Kameraet zoomer mykt inn på utvalgte detaljer og glir rolig tilbake til oversikt før neste detalj løftes frem.",
        }),
        createStep({
          start: 8, end: 12, kenBurns: { x: -0.42, y: 0.32, zoom: 1.9, effect: "spotlight" },
          tag: "DETALJ 1 (SPOTLIGHT)", tagColor: "#34D399", title: "Objektiv og linseelementer",
          body: "Kameraet er zoomet 1.9x inn på messingobjektivet med en behagelig spotlight-effekt som toner ned omgivelsene. Tekstboksen er plassert til høyre for motivet.",
        }),
        createStep({
          start: 2, end: 6, kenBurns: { x: 0.38, y: -0.22, zoom: 2.1, effect: "lens" },
          tag: "DETALJ 2 (LUPE)", tagColor: "#FBBF24", title: "Justeringsratt og sokkel",
          body: "Mellom forrige fokuspunkt og dette har bildet elegant glidd ut til 1.0x oversikt, før det rolig zoomer 2.1x inn på tannhjul og låseskruer, innrammet av en sirkulær lupe-ring.",
        }),
        createStep({
          start: 4, end: 10, kenBurns: { x: 0, y: 0.1, zoom: 1.0 },
          tag: "OVERSIKT", title: "Tilbake til helheten",
          body: "Kameraet trekker seg tilbake til standard 1.0x zoom slik at du ser hele atelieroppsettet før neste kapittel.",
        }),
      ],
    },
    {
      order: 2,
      type: "sticky",
      templateId: 5,
      title: "Niels Juels feltkamera",
      backgroundImage: "04RW1dGddh",
      backgroundFocus: { x: 0, y: 0 },
      gridSize: "twelveColumns",
      containers: [
        createStep({
          start: 4, end: 10, kenBurns: { x: 0, y: 0, zoom: 1.0 }, bg: "#000000C8",
          tag: "KAPITTEL 2", tagColor: "#60A5FA", title: "Bærbart feltkamera",
          body: "Gårdeier Niels Juel fra Hurdal var en ivrig hobbyfotograf tidlig på 1900-tallet. Hans kamera brukte glassplater i små esker.",
        }),
        createStep({
          start: 8, end: 12, kenBurns: { x: -0.38, y: 0.28, zoom: 1.85, effect: "blur" }, bg: "#000000D4",
          tag: "FOKUS: BELG & SKINNE (DYBDESKARPHET)", tagColor: "#34D399", title: "Sammenleggbar belgkonstruksjon",
          body: "Belgen i skinn fremheves med myk dybdeskarphet der bakgrunnen uskarpes rolig, slik at skinndetaljene trer tydelig frem.",
        }),
        createStep({
          start: 2, end: 6, kenBurns: { x: 0.36, y: -0.22, zoom: 2.15, effect: "pulse" }, bg: "#000000D4",
          tag: "FOKUS: LUKKERMEKANISME (MARKØR)", tagColor: "#F472B6", title: "Presisjonsmekanikk i messing",
          body: "Pulserende ringer markerer lukkerhjulet og blenderåpningen med nøyaktig fokus i 2.15x forstørrelse med en jevn to-trinns bue uten brå rykk.",
        }),
        createStep({
          start: 8, end: 12, kenBurns: { x: 0.05, y: 0.42, zoom: 1.95, effect: "spotlight" }, bg: "#000000D4",
          tag: "FOKUS: TOPP & LÆRHÅNDTAK (SPOTLIGHT)", tagColor: "#FBBF24", title: "Håndverket i tre og lær",
          body: "En diskret lyskjegle fremhever kassens fine trestruktur og solide lærreim med høy detaljrikdom.",
        }),
      ],
    },
    {
      order: 3,
      type: "content",
      templateId: 2,
      title: "Filmatisk bevegelse",
      backgroundColor: "#0B1120",
      containers: [
        {
          startColumn: 5,
          endColumn: 6,
          backgroundColor: "#1E293B",
          borderRadius: true,
          blocks: [
            { type: "text", text: createDraftText("HVORDAN VIRKER DET?", "FONT_SIZE_13", "left", "#93C5FD") },
            { type: "text", text: createDraftText("To-trinns bue: Zoom ut før neste nærbilde", "FONT_SIZE_24", "left", "#FFFFFF") },
            {
              type: "text",
              text: createDraftText(
                "Når to påfølgende tekstkort begge har nærbilde-zoom, vil en direkte overgang føles brå («sprette i mellom»). Vår nye Ken Burns-arkitektur gjenkjenner dette og animerer bildet rolig ut til 1.0x oversikt samtidig som fokuspunktet glir over, før kameraet zoomer inn på neste motiv. Dette gir seeren orientering og en behagelig dokumentarfølelse.",
                "FONT_SIZE_16",
                "left",
                "#CBD5E1"
              ),
            },
          ],
        },
      ],
    },
    {
      order: 4,
      type: "sticky",
      templateId: 5,
      title: "Historisk miljø og detaljer",
      backgroundImage: "013Aitiph5CL",
      backgroundFocus: { x: 0, y: 0 },
      gridSize: "twelveColumns",
      containers: [
        createStep({
          start: 4, end: 10, kenBurns: { x: 0, y: 0, zoom: 1.0 },
          tag: "KAPITTEL 3", tagColor: "#A78BFA", title: "Utforskning av miljøet",
          body: "Et historisk fotografi skjuler ofte små historier i krokene. La oss se nærmere på detaljene i bildet.",
        }),
        createStep({
          start: 8, end: 12, kenBurns: { x: -0.44, y: 0.32, zoom: 2.05, effect: "lens" },
          tag: "FOKUS: ØVRE VENSTRE (LUPE)", tagColor: "#34D399", title: "Bakgrunnsdetaljer",
          body: "Her forstørres den øvre venstre kvadranten 2.05x innrammet av en skarp lupe-ring.",
        }),
        createStep({
          start: 2, end: 6, kenBurns: { x: 0.42, y: -0.28, zoom: 2.2, effect: "pulse" },
          tag: "FOKUS: NEDRE HØYRE (MARKØR)", tagColor: "#FBBF24", title: "Forgrunnsgjenstander",
          body: "Kameraet zoomer ut til 1.0x overgang før det føres 2.2x inn mot nedre høyre hjørne med pulserende markørringer.",
        }),
        createStep({
          start: 4, end: 10, kenBurns: { x: 0, y: 0, zoom: 1.0 },
          tag: "SLUTT PÅ DEMO", title: "Full oversikt gjenopprettet",
          body: "Nå har du sett hvordan Ken Burns-effekten gir liv og dybde til stillbilder i Virtuelt Museum.",
        }),
      ],
    },
  ],
};
